import { Link } from 'react-router-dom';
import './ProductTable.css';

function ProductTable({
  products,
  isLoading,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onDelete,
  onDuplicate,
  sort,
  onSortChange,
}) {
  const allSelected = products.length > 0 && selectedIds.length === products.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < products.length;

  const handleSort = (field) => {
    const [currentField, currentOrder] = sort.split(':');
    if (currentField === field) {
      onSortChange(`${field}:${currentOrder === 'asc' ? 'desc' : 'asc'}`);
    } else {
      onSortChange(`${field}:asc`);
    }
  };

  const getSortIcon = (field) => {
    const [currentField, currentOrder] = sort.split(':');
    if (currentField !== field) {
      return (
        <svg className="sort-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    if (currentOrder === 'asc') {
      return (
        <svg className="sort-icon active" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
        </svg>
      );
    }
    return (
      <svg className="sort-icon active" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { label: 'Active', className: 'badge-success' },
      DRAFT: { label: 'Draft', className: 'badge-warning' },
      ARCHIVED: { label: 'Archived', className: 'badge-secondary' },
      OUT_OF_STOCK: { label: 'Out of Stock', className: 'badge-danger' },
    };
    const config = statusConfig[status] || statusConfig.DRAFT;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="product-table-container">
        <div className="table-loading">
          <div className="loading-spinner"></div>
          <span>Loading products...</span>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div className="product-table-container">
      <table className="product-table">
        <thead>
          <tr>
            <th className="checkbox-cell">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={(e) => onSelectAll(e.target.checked)}
                aria-label="Select all products"
              />
            </th>
            <th className="image-cell">Product</th>
            <th className="sku-column-cell">SKU</th>
            <th className="sortable" onClick={() => handleSort('status')}>
              Status {getSortIcon('status')}
            </th>
            <th className="sortable" onClick={() => handleSort('stock')}>
              Stock {getSortIcon('stock')}
            </th>
            <th className="sortable" onClick={() => handleSort('price')}>
              Price {getSortIcon('price')}
            </th>
            <th>Category</th>
            <th className="sortable" onClick={() => handleSort('createdAt')}>
              Created {getSortIcon('createdAt')}
            </th>
            <th className="actions-cell">Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product.id} className={selectedIds.includes(product.id) ? 'selected' : ''}>
              <td className="checkbox-cell">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(product.id)}
                  onChange={(e) => onSelectOne(product.id, e.target.checked)}
                  aria-label={`Select ${product.name}`}
                />
              </td>
              <td className="image-cell">
                <div className="product-image">
                  {product.images && product.images.length > 0 ? (
                    <>
                      <img
                        src={product.images.find((i) => i.isPrimary)?.url || product.images[0].url}
                        alt={product.name}
                        onError={(e) => {
                          // Handle broken image URLs by showing placeholder
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                      <div className="no-image" style={{ display: 'none' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                      </div>
                    </>
                  ) : (
                    <div className="no-image">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              </td>
              <td className="sku-column-cell">
                <div className="product-details">
                  <div className="product-name-wrapper">
                    <Link to={`/products/${product.id}`} className="product-name">
                      {product.name}
                    </Link>
                  </div>
                  {product.isFeatured && (
                    <div className="product-badge-wrapper">
                      <span className="badge badge-featured">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Featured
                      </span>
                    </div>
                  )}
                  <div className="product-sku-wrapper">
                    <span className="product-sku">{product.sku}</span>
                  </div>
                </div>
              </td>
              <td>{getStatusBadge(product.status)}</td>
              <td>
                <span className={`stock-value ${product.stock <= product.lowStockThreshold ? 'low-stock' : ''}`}>
                  {product.stock}
                  {product.stock <= product.lowStockThreshold && product.status === 'active' && (
                    <span className="low-stock-badge">Low</span>
                  )}
                </span>
              </td>
              <td className="price-cell">
                <span className="price">{formatPrice(product.price)}</span>
                {product.compareAtPrice && (
                  <span className="compare-price">{formatPrice(product.compareAtPrice)}</span>
                )}
              </td>
              <td>{product.category?.name || '-'}</td>
              <td className="date-cell">{formatDate(product.createdAt)}</td>
              <td className="actions-cell">
                <div className="actions">
                  <Link to={`/products/${product.id}`} className="action-btn" title="View">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </Link>
                  <Link to={`/products/${product.id}/edit`} className="action-btn" title="Edit">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                  </Link>
                  <button className="action-btn duplicate" onClick={() => onDuplicate(product.id)} title="Duplicate">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                    </svg>
                  </button>
                  <button className="action-btn delete" onClick={() => onDelete(product.id)} title="Delete">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ProductTable;
